<template>
    <div class="app--engagement-footer bg--footer border--footer" :class="classes">
        <div class="footer-progress" v-if="theme.footer.progress">
            <engagement-progress v-if="current > 0" :current="current" :total="total"></engagement-progress>
        </div>
        <div class="footer-navigation">
            <button v-if="hasPrev && (hasNext || preview)" class="prev" @click="prev" :disabled="loading">
                <app-icon key="prev" value="prev"></app-icon> {{ trans('buttons.previous') }}
            </button>
            <button v-if="!hasPrev && hasStart" class="start" @click="next" :disabled="loading">
                {{ trans('buttons.start') }} <app-icon key="start" value="start"></app-icon>
            </button>
            <button v-if="hasNext && (hasPrev || !hasStart) && !last" class="next" @click="next" :disabled="loading">
                {{ trans('buttons.next') }} <app-icon key="next" value="next"></app-icon>
            </button>
            <button v-if="last" class="finish" @click="finish" :disabled="loading">
                {{ trans('buttons.finish') }} <app-icon key="finish" value="finish"></app-icon>
            </button>
        </div>
        <div v-if="hasConfirm" v-show="confirm" :class="classesConfirm">
            <div class="title">{{ template.confirmation }}</div>
            <div class="d-flex justify-content-between">
                <button class="app--button primary" @click="confirmed">
                    {{ template.translations['buttons.finish'] }}
                </button>
                <button class="app--button muted" @click="confirm = loading = false">
                    {{ template.translations['buttons.cancel'] }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import store from '../../store'
import Progress from './../../../components/Progress'

export default {
    props: {
        pages: { type: Array, required: true }
    },
    computed: {
        theme() { return store.state.theme },
        template() { return store.state.template },
        page() { return store.state.page },
        index() { return this.page ? this.pages.map(p => p.id).indexOf(this.page.id) : null },
        hasStart() { return this.pages && this.pages.length > 0 && this.pages[0].type == 'START' },
        hasNext() { return this.index < this.pages.length - 1 },
        hasPrev() { return this.index > 0 },
        current() { return this.hasStart ? this.index : this.index + 1 },
        total() { return this.pages.filter(p => p.type != 'START').length },
        last() { return this.index == this.pages.length - 2 && !store.state.complete },
        preview() { return store.state.preview === true },
        secure() { return store.state.security === 'LOW' || store.state.auth.authenticated === true },
        hasConfirm() { return store.state.theme.confirm.enabled == true },
        classesConfirm() {
            let c = [
                'confirm',
                'bg--confirm',
                'border--confirm',
                'text--color-' + this.theme.confirm.color,
                'text--font-' + this.theme.confirm.font,
                'text--size-' + this.theme.confirm.size,
            ];
            return c
        },
        classes() {
            let c = [
                'text--color-' + this.theme.footer.color,
                'text--font-' + this.theme.footer.font,
                'text--size-' + this.theme.footer.size,
            ];
            if (this.theme.footer.fixed) { c.push('fixed') }
            if (!this.theme.footer.progress) { c.push('justify-content-end') }

            return c
        },
        element() {
            return store.state.elements.find(e => {
                let element = store.getters['customElement']('cfs_report')
                return element && e.definition == element.id
            })
        },
        name() {
            let fname = store.state.elements.find(e => {
                let element = store.getters['customElement']('student_first_name')
                return element && e.definition == element.id
            })

            let lname = store.state.elements.find(e => {
                let element = store.getters['customElement']('student_last_name')
                return element && e.definition == element.id
            })

            let result = ''
            if (fname && fname.value) result = fname.value
            if (lname && lname.value) result += ' ' + lname.value

            return result
        },
        values() {
            let result = { student: this.name }

            store.getters['customElements']().forEach(e => {
                if (e && e.id) {
                    let el = store.state.elements.find(el => el.definition == e.id)
                    if (el && el.value) {
                        result[e.options.custom_id] = this._formatValue(e.type, el.value)
                    }
                }
            })
            return result
        }
    },
    data() {
        return {
            loading: false,
            confirm: false,
            closing: false
        }
    },
    methods: {
        trans(key) {
            return store.getters['trans'](key) || this.$t('front.' + key)
        },
        prev() {
            this.loading = true
            if (!this.secure) {
                store.dispatch('auth/start').then(() => { this.loading = false })
            } else {
                let parent = this.$parent
                let element = null
                if (parent && parent.$refs && parent.$refs[this.page.id] && parent.$refs[this.page.id].$el) {
                    element = this.$validatePage(parent.$refs[this.page.id].$el)
                }
                if (store.getters['validateEachPanel'] == false || element === null) {
                    store.commit('SET_PAGE', this.pages[this.index - 1])
                    this.$nextTick(() => {
                        window.scrollTo(0, 1)
                        this.loading = false
                    })
                } else if (element !== null) {
                    this.$nextTick(() => {
                        element.scrollIntoView(false)
                        this.loading = false
                    })
                }
            }
        },
        next(event) {
            this.loading = true
            if (!this.secure) {
                store.dispatch('auth/start').then(() => { this.loading = false })
            } else {
                let parent = this.$parent
                let element = null
                if (parent && parent.$refs && parent.$refs[this.page.id] && parent.$refs[this.page.id].$el) {
                    element = this.$validatePage(parent.$refs[this.page.id].$el)
                }
                if (store.getters['validateEachPanel'] == false || element === null) {
                    store.commit('SET_PAGE', this.pages[this.index + 1])
                    this.$nextTick(() => {
                        window.scrollTo(0, 1)
                        this.loading = false
                    })
                } else if (element !== null) {
                    this.$nextTick(() => {
                        element.scrollIntoView(false)
                        this.loading = false
                    })
                }
            }
        },
        finish(event) {
            event.target.blur()
            if (this.hasConfirm === true) { this.confirm = true } else { this.complete() }
        },
        confirmed() {
            this.confirm = false
            this.complete()
        },
        complete() {
            if (this.preview) {
                store.commit('SET_PAGE', this.pages.find(p => p.type === 'FINISH'))
                return
            }
            let parent = this.$parent
            let element = null
            for (let i = 0; i < this.pages.length; i++) {
                let page = this.pages[i]
                if (parent && parent.$refs && parent.$refs[page.id] && parent.$refs[page.id].$el) {
                    element = this.$validatePage(parent.$refs[page.id].$el)
                    if (element !== null) {
                        store.commit('SET_PAGE', page)
                        this.$nextTick(() => {
                            element.scrollIntoView(false)
                        })
                        return
                    }
                }
            }

            this.loading = true
            this.closing = true
            this.report()
        },
        report() {
            return axios.post('/e/cfs/report', { id: store.state.id, values: this.values }).then(response => {
                store.commit('QUEUE', { id: this.element.id, group: this.element.group, value: response.data.report })
                store.dispatch('save').then(response => {
                    store.dispatch('close').then(() => {
                        this.loading = false
                        this.closing = false
                        window.scrollTo(0, 1)
                    })
                })
            }).catch(error => {
                let element = this.element
                element.value = error.response.data
                element.invalid = true
                store.commit('ADD_ELEMENT', element)
                store.commit('SET_PAGE', this.pages.slice(-1)[0])
                store.commit('SET_LOADING', false)
                store.commit('SET_COMPLETE')
                this.loading = false
                this.closing = false
            })
        },
        _formatValue(type, value) {
            if (type == 'CURRENCY') {
                return parseFloat(value).toFixed(0);
            }
            if (type == 'ADDRESS') {
                return value.state;
            }
            if (type == 'OPTION') {
                return value[0];
            }
            if (type == 'DATE') {
                let d = value.substring(0, 10).split("-")
                if (d.length == 3) {
                    return d[0] + '/' + d[1] + '/' + d[2]
                }
                return ''
            }

            return value;
        },
    },
    components: {
        'engagement-progress': Progress
    }
}
</script>
